import React from "react";
import Typography from "../../../common/Typography";
import Button from "../../../common/Button";
import { ResponsiveImage, MarkdownWrapper } from "../../../common";
import { RevealTextStripContent } from "../../../../types/newtypes";
import { useRevealTextStripHook } from "./hooks/useRevealTextStripHook";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";

interface RevealTextStripPreviewProps {
  content: RevealTextStripContent;
  onCompleted?: () => void;
  onCardStateChange?: (isCompleted: boolean, canProceed: boolean) => void;
}

const RevealTextStripPreview: React.FC<RevealTextStripPreviewProps> = ({
  content,
  onCompleted,
  onCardStateChange,
}) => {
  const {
    selectedItemIndex,
    handleItemReveal,
    allItemsRevealed,
    remainingItems,
    completedItems,
    // Scroll-related properties
    scrollContainerRef,
    showScrollIndicators,
    canScrollLeft,
    canScrollRight,
    handleScroll,
    scrollLeft,
    scrollRight,
  } = useRevealTextStripHook({
    content,
    onCardStateChange,
  });

  return (
    <div className="w-full">
      {/* Title */}
      <div className="mb-8">
        <Typography variant="h4" weight="bold" className="mb-4 max-md:!text-xl">
          {content?.slide_title}
        </Typography>
      </div>

      {/* Top Section - Horizontal Icon Cards */}
      <div className="relative mb-6">
        {/* Scroll Indicators */}
        {showScrollIndicators && (
          <>
            {/* Left Scroll Button */}
            {canScrollLeft && (
              <Button
                onClick={scrollLeft}
                variant="filled"
                size="none"
                className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 w-8 h-8 !rounded-full shadow-md transition-colors"
              >
                <ChevronLeftIcon className="w-4 h-4 text-gray-600" />
              </Button>
            )}

            {/* Right Scroll Button */}
            {canScrollRight && (
              <Button
                onClick={scrollRight}
                variant="filled"
                size="none"
                className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 w-8 h-8 !rounded-full shadow-md transition-colors"
              >
                <ChevronRightIcon className="w-4 h-4 text-gray-600" />
              </Button>
            )}
          </>
        )}

        {/* Scroll Container */}
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="flex gap-4 overflow-x-auto scrollbar-hide"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          {content?.items?.map((item, index) => {
            return (
              <button
                key={index}
                onClick={() => handleItemReveal(index)}
                className={`flex-shrink-0 lg:w-1/6 lg:h-[w-1/6] md:w-1/3 md:h-[w-1/3] sm:w-1/2 sm:h-[w-1/2] w-full h-[w-full] rounded-lg min-h-40 ${
                  completedItems.has(index)
                    ? "opacity-100"
                    : "opacity-80 hover:opacity-100"
                }`}
              >
                <div className="w-full h-full flex flex-col items-center justify-center">
                  {/* Icon */}
                  <div
                    className={`w-full h-full p-2 rounded-lg overflow-hidden border-2 transition-all duration-200 ${
                      selectedItemIndex === index
                        ? "border-blue-500 bg-blue-50 shadow-md"
                        : "border-gray-200 bg-gray-50 hover:border-gray-300"
                    }`}
                  >
                    <ResponsiveImage
                      image_uri={item?.image_uri || item?.icon_uri}
                      alt={`Icon for ${item?.label || item?.prompt}`}
                      maxWidth="100%"
                      maxHeight="100%"
                      objectFit="cover"
                      containerClassName="w-full h-full rounded-lg"
                      className="w-full h-full object-cover"
                      showErrorState={true}
                    />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      {/* Bottom Section - Instructions (context-appropriate for device) */}
      <div className="pb-2">
        <Typography variant="small" className="hidden md:block">
          Use Tab to navigate between options and Enter to select
        </Typography>
        <Typography variant="small" className="block md:hidden">
          Tap each icon to reveal its content
        </Typography>
      </div>
      {/* Middle Section - Content Panel */}
      <div className="bg-background rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        {selectedItemIndex !== null ? (
          // Show content for the selected item
          (() => {
            const selectedItem = content?.items[selectedItemIndex];

            return (
              <div>
                <div className="mb-3 max-md:!text-lg">
                  <MarkdownWrapper content={selectedItem?.label || selectedItem?.prompt || ""} />
                </div>
                <div className="prose prose-sm max-w-none">
                  <MarkdownWrapper
                    content={selectedItem?.revealed_text || ""}
                  />
                </div>
              </div>
            );
          })()
        ) : (
          // Show placeholder when no items are revealed
          <div className="text-center py-8">
            <Typography variant="p" className="text-gray-500">
              Click on an icon above to reveal its content
            </Typography>
          </div>
        )}
      </div>

      {/* Progress/Completion Status */}
      {!allItemsRevealed ? (
        <div className="mt-2 p-2 text-xs bg-blue-50 border border-blue-200 rounded-lg inline-block max-sm:mb-4">
          <Typography variant="small" className="text-blue-800 text-center">
            Reveal all cards to proceed to next. {remainingItems} card
            {remainingItems !== 1 ? "s" : ""} remaining...
          </Typography>
        </div>
      ) : (
        <div className="mb-2 p-2 text-xs bg-green-50 border border-green-200 rounded-lg inline-block max-sm:mb-4">
          <Typography variant="small" className="text-green-800 text-center">
            All items have been revealed! You can now proceed.
          </Typography>
        </div>
      )}
    </div>
  );
};

export default RevealTextStripPreview;
